/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    Box,
    Text,
    Image,
    Flex,
    VStack,
    HStack,
    Button,
    IconButton,
    Input,
    Select,
    Textarea,
    FormControl,
    FormLabel,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    useToast,
    Spinner,
    CloseButton,
} from '@chakra-ui/react';
import { BiCamera, BiMinus, BiPlus } from 'react-icons/bi';
import { uploadImage } from 'redux/actions/storeActions';
import { useDispatch, useSelector } from 'react-redux';
import { initiateProductReturn } from 'redux/actions/orderActions';
import WalletHistoryPrice from 'components/Wallet/WalletHistoryPrice';

// Return reasons - you can move this to a separate utils file if needed
const returnReasons = [
    'Missing item(s) in the pack',
    'Wrong item(s) delivered',
    'Damaged or defective item(s)',
    'Wrong size(s) received',
    'Wrong color(s) received',
    'Quality not as expected',
    'Pack or bundle not as described',
    'Ordered by mistake',
    "Didn't approve this order",
    'Order arrived late',
    'No longer needed',
    'Found it cheaper elsewhere',
    'Incomplete bundle',
    'Delivered to wrong address',
    'Packaging was opened or tampered',
];
export const SingleProductReturnModal = ({
    isOpen,
    onClose,
    lineItem,
    orderId,
    orderRef,

    isSubmitting,
}) => {
    const { loadingProductReturn, productReturn, error } = useSelector(
        (state) => state.orderReducer,
    );
    const dispatch = useDispatch();
    const [returnQuantity, setReturnQuantity] = useState(1);
    const [returnReason, setReturnReason] = useState('');
    const [comment, setComment] = useState('');
    const [productImage, setProductImage] = useState(null);

    const toast = useToast();

    const handleImageUpload = (files) => {
        if (!files || files.length === 0) return;

        const file = files[0];
        if (file.size > 5 * 1024 * 1024) {
            toast({
                title: 'Error',
                description: 'Image size must be less than 5MB',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        if (!file.type.startsWith('image/')) {
            toast({
                title: 'Error',
                description: 'Only image files are allowed',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        const preview = URL.createObjectURL(file);
        setProductImage({
            file,
            preview,
            name: file.name,
        });
    };

    const removeImage = () => {
        if (productImage?.preview) {
            URL.revokeObjectURL(productImage.preview);
        }
        setProductImage(null);
    };

    const handleSubmit = async () => {
        if (!returnReason) {
            toast({
                title: 'Error',
                description: 'Please select a return reason',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            let uploadedImageUrl = null;

            if (productImage) {
                try {
                    const imgResponse = await uploadImage(productImage.file);
                    uploadedImageUrl = imgResponse.secure_url;
                } catch (error) {
                    console.error('Failed to upload image:', error);
                    toast({
                        title: 'Error',
                        description: 'Failed to upload image. Please try again.',
                        status: 'error',
                        duration: 3000,
                        isClosable: true,
                    });
                    return;
                }
            }

            const unitPrice = parseFloat(lineItem?.custom_field_hash?.cf_item_price_unformatted);
            const totalPrice = unitPrice * returnQuantity;

            const payload = {
                order_id: orderId,
                reason: JSON.stringify({
                    return_reason: returnReason,
                    comment: comment,
                    order_ref: orderRef,
                    productData: {
                        item_id: lineItem?.item_id,
                        quantity: returnQuantity,
                        name: lineItem?.group_name,
                        rate: unitPrice.toString(),
                        total_price: totalPrice.toString(),
                        imageurl: uploadedImageUrl ? JSON.stringify([uploadedImageUrl]) : '',
                        original_image_url: lineItem?.brand_image || '',
                    },
                }),
                products: [
                    {
                        item_id: lineItem?.item_id,
                        quantity: returnQuantity,
                    },
                ],
            };

            dispatch(initiateProductReturn(payload));
        } catch (error) {
            console.error('Error submitting return:', error);
            toast({
                title: 'Error',
                description: 'Failed to submit return request. Please try again.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };
    useEffect(() => {
        if (productReturn) {
            toast({
                title: 'Success',
                description: `Return request submitted for ${returnQuantity} item(s)!`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            onClose();

            // Reset form
            setReturnQuantity(1);
            setReturnReason('');
            setComment('');
            removeImage();
        }
    }, [productReturn]);

    if (error) {
        toast({
            title: 'Error',
            description: error,
            status: 'error',
            duration: 3000,
            isClosable: true,
        });
    }

    const isFormValid = returnReason.trim() !== '';

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md">
            <ModalOverlay bg="blackAlpha.300" />
            <ModalContent>
                <ModalHeader fontSize="sm" fontWeight="semibold" pb={2}>
                    Return Product
                </ModalHeader>
                <ModalCloseButton />

                <ModalBody pb={6}>
                    <VStack spacing={6} align="stretch">
                        {/* Product Info */}
                        <Box border="1px" borderColor="gray.200" borderRadius="lg" p={4}>
                            <Flex align="center" gap={4}>
                                {lineItem?.brand_image && (
                                    <Image
                                        src={lineItem?.brand_image}
                                        alt={lineItem?.brand}
                                        boxSize="64px"
                                        objectFit="cover"
                                        borderRadius="md"
                                    />
                                )}
                                <Box flex={1}>
                                    <Text fontSize="xs" color="blue.500">
                                        {lineItem?.category_name}
                                    </Text>
                                    <Text fontWeight="medium">{lineItem?.brand}</Text>
                                    <Text fontSize="sm" color="gray.600">
                                        {lineItem?.group_name}
                                    </Text>
                                    <Text fontSize="sm">Available: {lineItem?.quantity} items</Text>
                                </Box>
                                <Box textAlign="right">
                                    <Text fontWeight="medium">
                                        <WalletHistoryPrice price={lineItem?.item_total} />
                                    </Text>
                                </Box>
                            </Flex>
                        </Box>

                        {/* Quantity Selection */}
                        {lineItem?.quantity > 1 && (
                            <FormControl>
                                <FormLabel fontSize="sm" fontWeight="medium" mb={2}>
                                    Quantity to return:
                                </FormLabel>
                                <HStack spacing={3}>
                                    <IconButton
                                        aria-label="Decrease quantity"
                                        icon={<BiMinus />}
                                        size="sm"
                                        onClick={() =>
                                            setReturnQuantity(Math.max(1, returnQuantity - 1))
                                        }
                                        isDisabled={returnQuantity <= 1}
                                    />

                                    <NumberInput
                                        min={1}
                                        max={lineItem?.quantity}
                                        value={returnQuantity}
                                        onChange={(_, value) => {
                                            const newValue = Math.max(
                                                1,
                                                Math.min(lineItem?.quantity, value || 1),
                                            );
                                            setReturnQuantity(newValue);
                                        }}
                                        size="sm"
                                        width="80px">
                                        <NumberInputField textAlign="center" />
                                        <NumberInputStepper>
                                            <NumberIncrementStepper />
                                            <NumberDecrementStepper />
                                        </NumberInputStepper>
                                    </NumberInput>

                                    <IconButton
                                        aria-label="Increase quantity"
                                        icon={<BiPlus />}
                                        size="sm"
                                        onClick={() =>
                                            setReturnQuantity(
                                                Math.min(lineItem?.quantity, returnQuantity + 1),
                                            )
                                        }
                                        isDisabled={returnQuantity >= lineItem?.quantity}
                                    />

                                    <Text fontSize="sm" color="gray.600">
                                        of {lineItem?.quantity}
                                    </Text>
                                </HStack>

                                <HStack mt={2} spacing={2}>
                                    <Button
                                        size="xs"
                                        variant={returnQuantity === 1 ? 'solid' : 'outline'}
                                        colorScheme={returnQuantity === 1 ? 'blue' : 'gray'}
                                        onClick={() => setReturnQuantity(1)}>
                                        1
                                    </Button>
                                    {lineItem?.quantity >= 2 && (
                                        <Button
                                            size="xs"
                                            variant={
                                                returnQuantity ===
                                                Math.floor(lineItem?.quantity / 2)
                                                    ? 'solid'
                                                    : 'outline'
                                            }
                                            colorScheme={
                                                returnQuantity ===
                                                Math.floor(lineItem?.quantity / 2)
                                                    ? 'blue'
                                                    : 'gray'
                                            }
                                            onClick={() =>
                                                setReturnQuantity(
                                                    Math.floor(lineItem?.quantity / 2),
                                                )
                                            }>
                                            Half ({Math.floor(lineItem?.quantity / 2)})
                                        </Button>
                                    )}
                                    <Button
                                        size="xs"
                                        variant={
                                            returnQuantity === lineItem?.quantity
                                                ? 'solid'
                                                : 'outline'
                                        }
                                        colorScheme={
                                            returnQuantity === lineItem?.quantity ? 'blue' : 'gray'
                                        }
                                        onClick={() => setReturnQuantity(lineItem?.quantity)}>
                                        All ({lineItem?.quantity})
                                    </Button>
                                </HStack>
                            </FormControl>
                        )}

                        {/* Return Reason */}
                        <FormControl isRequired>
                            <FormLabel fontSize="sm" fontWeight="medium" mb={2}>
                                Reason for Return
                            </FormLabel>
                            <Select
                                placeholder="Select a reason..."
                                value={returnReason}
                                onChange={(e) => setReturnReason(e.target.value)}
                                focusBorderColor="blue.500">
                                {returnReasons.map((reason) => (
                                    <option key={reason} value={reason}>
                                        {reason}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Additional Comments */}
                        <FormControl>
                            <FormLabel fontSize="sm" fontWeight="medium" mb={2}>
                                Additional Comments (Optional)
                            </FormLabel>
                            <Textarea
                                rows={3}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Please provide any additional details..."
                                focusBorderColor="blue.500"
                            />
                        </FormControl>

                        {/* Image Upload */}
                        <FormControl>
                            <FormLabel fontSize="sm" fontWeight="medium" mb={2}>
                                Add Photo (Optional)
                            </FormLabel>
                            <Text fontSize="xs" color="gray.600" mb={3}>
                                Upload a photo to help describe the issue with your product
                            </Text>

                            {!productImage ? (
                                <HStack spacing={3}>
                                    <Box>
                                        <Button
                                            as="label"
                                            leftIcon={<BiCamera />}
                                            size="sm"
                                            variant="outline"
                                            cursor="pointer"
                                            _hover={{ bg: 'gray.50' }}>
                                            Choose Photo
                                            <Input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleImageUpload(e.target.files)}
                                                display="none"
                                            />
                                        </Button>
                                    </Box>
                                    <Text fontSize="xs" color="gray.600">
                                        Max 5MB, JPG/PNG only
                                    </Text>
                                </HStack>
                            ) : (
                                <HStack align="start" spacing={3}>
                                    <Box position="relative">
                                        <Image
                                            src={productImage.preview}
                                            alt="Return evidence"
                                            boxSize="80px"
                                            objectFit="cover"
                                            borderRadius="lg"
                                            border="1px"
                                            borderColor="gray.200"
                                        />
                                        <CloseButton
                                            position="absolute"
                                            top="-8px"
                                            right="-8px"
                                            size="sm"
                                            bg="red.500"
                                            color="white"
                                            borderRadius="full"
                                            _hover={{ bg: 'red.600' }}
                                            onClick={removeImage}
                                        />
                                    </Box>
                                    <Box flex={1}>
                                        <Text fontSize="sm" fontWeight="medium">
                                            {productImage.name}
                                        </Text>
                                        <Text fontSize="xs" color="gray.600" mt={1}>
                                            Click the x to remove or upload a different photo
                                        </Text>
                                        <Button
                                            as="label"
                                            leftIcon={<BiCamera />}
                                            size="xs"
                                            variant="ghost"
                                            mt={2}
                                            cursor="pointer">
                                            Change Photo
                                            <Input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleImageUpload(e.target.files)}
                                                display="none"
                                            />
                                        </Button>
                                    </Box>
                                </HStack>
                            )}
                        </FormControl>

                        {/* Submit Buttons */}
                        <Box pt={6} borderTop="1px" borderColor="gray.200">
                            <HStack justify="flex-end" spacing={4}>
                                <Button
                                    variant="outline"
                                    onClick={onClose}
                                    isDisabled={isSubmitting || loadingProductReturn}>
                                    Cancel
                                </Button>
                                <Button
                                    colorScheme="blue"
                                    onClick={handleSubmit}
                                    isDisabled={
                                        !isFormValid || isSubmitting || loadingProductReturn
                                    }
                                    isLoading={isSubmitting || loadingProductReturn}
                                    loadingText="Submitting..."
                                    spinner={<Spinner size="sm" />}>
                                    Submit Return Request
                                </Button>
                            </HStack>
                        </Box>
                    </VStack>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};
